<template>
    <SidebarProvider class="bg-background text-foreground">
        <a
            href="#org-main"
            class="bg-background focus:ring-ring sr-only z-50 rounded-md p-3 focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:ring-2"
            >Skip to content</a
        >
        <OrgSidebar
            :navigation="
                navigation.map((item) => ({
                    ...item,
                    active: active(item.url),
                }))
            "
        />
        <SidebarInset class="min-w-0">
            <header
                class="bg-background flex h-16 shrink-0 items-center gap-3 border-b px-4 lg:px-8"
            >
                <SidebarTrigger />
                <Separator orientation="vertical" class="!h-4" />
                <span class="text-muted-foreground text-sm">Organization</span>
                <ChevronRight
                    class="text-muted-foreground size-3.5"
                    aria-hidden="true"
                />
                <span class="text-sm font-medium">{{ currentSection }}</span>
                <Badge variant="secondary" class="ml-auto gap-1.5"
                    ><ShieldCheck
                        class="size-3"
                        aria-hidden="true"
                    />Staff</Badge
                >
            </header>
            <main
                id="org-main"
                tabindex="-1"
                class="min-w-0 flex-1 outline-none"
            >
                <slot />
            </main>
        </SidebarInset>
    </SidebarProvider>
</template>
<script setup lang="ts">
import { usePage } from '@inertiajs/vue3';
import {
    Activity,
    ChevronRight,
    FileText,
    FolderTree,
    LayoutDashboard,
    ShieldCheck,
    Tags,
    Users,
} from '@lucide/vue';
import { computed } from 'vue';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
    SidebarInset,
    SidebarProvider,
    SidebarTrigger,
} from '@/components/ui/sidebar';
import OrgSidebar from '@/components/org/OrgSidebar.vue';
import { useOrg } from '@/composables/useOrg';
import { dashboard, activity } from '@/lib/org/routes';
import { index as users } from '@/lib/org/users';
import { index as staffIndex } from '@/lib/org/staff';
import { index as posts } from '@/lib/org/posts';
import { index as taxonomy } from '@/lib/org/taxonomy';
const { can } = useOrg();
const page = usePage();
const navigation = computed(() =>
    [
        {
            label: 'Overview',
            url: dashboard().url,
            icon: LayoutDashboard,
            show: true,
        },
        {
            label: 'Users',
            url: users().url,
            icon: Users,
            show: can('users:view'),
        },
        {
            label: 'Staff',
            url: staffIndex().url,
            icon: ShieldCheck,
            show: can('staff:manage'),
        },
        {
            label: 'Posts',
            url: posts().url,
            icon: FileText,
            show: can('content:view'),
        },
        {
            label: 'Categories',
            url: taxonomy('categories').url,
            icon: FolderTree,
            show: can('taxonomy:manage'),
        },
        {
            label: 'Tags',
            url: taxonomy('tags').url,
            icon: Tags,
            show: can('taxonomy:manage'),
        },
        {
            label: 'Activity',
            url: activity().url,
            icon: Activity,
            show: can('activity:view'),
        },
    ].filter((item) => item.show),
);
function active(url: string) {
    const path = page.url.split('?')[0];
    return (
        path === url || (url !== dashboard().url && path.startsWith(`${url}/`))
    );
}
const currentSection = computed(
    () =>
        navigation.value.find((item) => active(item.url))?.label ?? 'Workspace',
);
</script>
